/**
 * nav-feedback.js — zichtbare feedback op een klik die navigeert.
 *
 * Achtergrond: tussen een klik op een productkaart en de eerste zichtbare pixel
 * van de volgende pagina zit op mobiel (4G, 4x CPU) tot ~1760 ms, waarvan
 * ~1100 ms wachten op het serverantwoord. In die hele periode staat de oude
 * pagina bevroren in beeld zonder enige bevestiging dat de tik is aangekomen.
 *
 * Het wegnemen van die wachttijd doet Shopify zelf al, via platform-brede
 * speculation rules voor Horizon (zie snippets/nav-feedback.liquid). Die werken
 * alleen op Chromium: 43,4% van de sessies. WebKit is 55,8% en krijgt er niets
 * van — inclusief Chrome op iOS, dat van Apple verplicht WebKit gebruikt en dus
 * niet meetelt als Chromium. Ook op Chromium mist `moderate` bij een snelle klik.
 *
 * Dit bestand is daarmee de enige laag die op een iPhone iets doet.
 *
 * Waarom de feedback op pointerdown start en niet op click
 * --------------------------------------------------------
 * Op een echte iPhone geverifieerd: Safari schildert niets meer zodra de
 * navigatie is gestart. De DOM verandert wel, maar het scherm bevriest. Een
 * balk die pas ná de klik verschijnt, komt daar dus nooit in beeld — precies
 * wat we zagen: in Chrome zichtbaar, op iOS niet.
 *
 * Daarom armeren we bij vingerdruk. Tussen neerzetten en loslaten zit typisch
 * 80-150 ms; wordt de balk in dat venster geschilderd, dan staat hij al op het
 * scherm wanneer Safari het beeld bevriest en blijft hij staan tot de nieuwe
 * pagina komt. Een bevroren balk is nog altijd feedback; een lege pagina niet.
 *
 * De keerzijde van vroeg armeren is een flits bij elke veeg die op een kaart
 * begint. Vandaar de korte vertraging plus annuleren op beweging en
 * pointercancel: een scroll is dan al herkend voordat er iets is getekend.
 *
 * De grens van vroeg armeren op iOS
 * ----------------------------------
 * Wat er in dat venster gebeurt, mag de pagina niet veranderen. Op toestel
 * gemeten, bij een tik van 62 ms:
 *
 *     63ms touchend
 *     67ms mousemove
 *     71ms +1 node op body        <- de balk, ingehangen na 70 ms
 *     (geen mousedown, geen click)
 *
 * iOS stuurt de gesynthetiseerde muisreeks pas ná touchend en breekt hem af
 * wanneer de pagina op dat moment verandert. Een snelle tik is korter dan de
 * armeer-vertraging, dus viel onze DOM-wijziging er precies bovenop; een trage
 * tik niet. Vandaar op touch: de balk meteen inhangen (met de aanloop in de
 * CSS-animatie, zodat een veeg nog steeds niets tekent), en de kaart tijdens
 * de aanraking onaangeroerd laten — de 'bezig'-staat komt pas bij de klik.
 * Zelfde reden waarom de pressed-state in ux-fixes.css op touch `filter`
 * gebruikt en geen `transform`: dat verandert geen enkele afmeting.
 *
 * Let op: dit maakt alleen déze laag onschadelijk. Er is daarnaast een bredere
 * "eerste tik doet niets" op categoriepagina's die óók op live optreedt, zonder
 * deze bestanden; die wordt apart behandeld.
 *
 * Wat hier NIET zit: de pressed-state bij vingerdruk. Die staat in CSS
 * (ux-fixes.css), omdat `:active` zonder JS-latency reageert.
 */
(function () {
  'use strict';

  /** Kort genoeg om binnen de vingerdruk te vallen, lang genoeg om een scroll te herkennen. */
  const ARM_DELAY_MS = 70;

  /** Vinger losgelaten maar geen klik gevolgd (long-press, buiten de link): opruimen. */
  const CLICK_GRACE_MS = 500;

  /**
   * Verplaatsing waarboven we de aanraking als scroll beschouwen.
   *
   * Ruim gekozen: een echte tik met de duim schuift zo tien pixels, en dan
   * annuleerde de feedback onterecht. De autoriteit voor "dit werd een scroll"
   * is `pointercancel` (de browser neemt het over) en het scroll-event; deze
   * drempel is alleen een vroege uitweg voor een sleep die nog geen scroll is.
   */
  const MOVE_CANCEL_PX = 24;

  /** Noodrem: gaat de navigatie toch niet door, dan de staat weer opruimen. */
  const BAR_MAX_MS = 15000;

  /**
   * iOS kan de click droppen als een carrousel tussen touchend en click de DOM
   * wijzigt (aria-hidden / snap). Als de click uitblijft, navigeren we zelf.
   */
  const TOUCH_NAV_FALLBACK_MS = 120;

  /** @type {HTMLElement | null} */
  let bar = null;
  /** @type {Element | null} */
  let busyElement = null;
  /** @type {Element | null} */
  let pendingAnchor = null;
  let confirmed = false;
  let startX = 0;
  let startY = 0;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let armTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let graceTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let killTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let touchNavTimer;

  /**
   * @param {string} href
   * @returns {string} URL zonder fragment, voor vergelijking met de huidige pagina
   */
  function stripHash(href) {
    const url = new URL(href, location.href);
    url.hash = '';
    return url.href;
  }

  /**
   * Volgt uit deze link een echte documentnavigatie? Zo nee, dan is er geen
   * wachttijd om te overbruggen en zou feedback misleidend zijn.
   *
   * @param {Element | null | undefined} anchor
   * @returns {boolean}
   */
  function navigatesAway(anchor) {
    if (!(anchor instanceof HTMLAnchorElement)) return false;
    if (!anchor.href) return false;
    if (anchor.hasAttribute('download')) return false;
    if (anchor.target && anchor.target !== '_self') return false;
    if (anchor.hasAttribute('data-no-nav-feedback')) return false;

    let url;
    try {
      url = new URL(anchor.href, location.href);
    } catch {
      return false;
    }

    if (url.origin !== location.origin) return false;
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;

    // Alleen-hash navigatie blijft op dezelfde pagina.
    if (stripHash(url.href) === stripHash(location.href)) return false;

    return true;
  }

  /**
   * De aangeklikte link is vaak een transparante overlay over een kaart; die
   * dimmen levert niets zichtbaars op. Markeer daarom de dichtstbijzijnde
   * visuele container, en val terug op het element zelf voor losse links.
   */
  const CARD_CONTAINERS =
    'product-card, .product-card, .card, .collection-card, .resource-card, .product-grid__item, .resource-list__item';

  /**
   * Besturingselementen die binnen een link kunnen staan. De carrousel-pijlen op
   * een productkaart zijn het geval waar het misging: `card-gallery.liquid` zet de
   * hele slideshow, inclusief pijlen, in de link naar de PDP. Een klik daarop
   * navigeert niet — `slideshow.js` vangt hem af — maar dat is pas te zien nadat
   * de handlers van de pagina hebben gedraaid, en die draaien ná onze
   * capture-listener. Zonder deze uitzondering bleef de kaart 15 seconden
   * uitgegrijsd staan (SHO-9537).
   */
  const CONTROLS_INSIDE_LINK =
    'button, [role="button"], input, select, textarea, summary, label, [contenteditable=""], [contenteditable="true"]';

  /**
   * Zit het aangeklikte element in een besturingselement binnen deze link?
   *
   * @param {EventTarget | null} target
   * @param {Element | null} anchor
   * @returns {boolean}
   */
  function controlIntercepts(target, anchor) {
    if (!(target instanceof Element) || !(anchor instanceof Element)) return false;
    const control = target.closest(CONTROLS_INSIDE_LINK);
    return control !== null && anchor.contains(control);
  }

  /**
   * Toont de feedback. Idempotent: een tweede aanroep vanuit de click-handler
   * mag niets opnieuw opbouwen.
   *
   * @param {Element} element
   * @param {boolean} [barOnly] Alleen de balk, de kaart onaangeroerd laten.
   */
  function showFeedback(element, barOnly) {
    clearTimeout(armTimer);

    if (!busyElement && !barOnly) {
      busyElement = element.closest(CARD_CONTAINERS) || element;
      busyElement.setAttribute('data-navigating', '');
    }

    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'hds-nav-progress';
      // Puur decoratief: de voortgang is niet bekend en een live region zou
      // schermlezers onderbreken tijdens een navigatie die ze zelf aankondigen.
      bar.setAttribute('aria-hidden', 'true');
      // In <body>, niet in <html>: een element buiten de body wordt in WebKit
      // niet gerenderd, waardoor de CSS-animatie op scaleX(0) blijft staan.
      document.body.append(bar);
    }
  }

  function clearFeedback() {
    clearTimeout(armTimer);
    clearTimeout(graceTimer);
    clearTimeout(killTimer);
    clearTimeout(touchNavTimer);

    pendingAnchor = null;
    confirmed = false;

    if (bar) {
      bar.remove();
      bar = null;
    }
    if (busyElement) {
      busyElement.removeAttribute('data-navigating');
      busyElement = null;
    }
  }

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (!event.isPrimary || event.button !== 0) return;

      const target = /** @type {Element | null} */ (event.target);
      const anchor = target && typeof target.closest === 'function' ? target.closest('a[href]') : null;
      if (!navigatesAway(anchor)) return;
      // Vóór `pendingAnchor`: anders zou de touch-fallback in de pointerup-handler
      // een tik op een carrousel-pijl alsnog als navigatie afmaken.
      if (controlIntercepts(target, anchor)) return;

      clearFeedback();
      pendingAnchor = anchor;
      startX = event.clientX;
      startY = event.clientY;

      const touch = event.pointerType === 'touch' || event.pointerType === 'pen';

      if (touch) {
        /*
         * Op toestel gemeten, bij een tik van 62 ms:
         *
         *     63ms touchend
         *     67ms mousemove
         *     71ms +1 node op body        <- onze balk
         *     (geen mousedown, geen click)
         *
         * iOS stuurt de muisreeks pas ná touchend en kijkt bij die eerste
         * `mousemove` of de pagina verandert. Zo ja, dan stopt de reeks en komt
         * er geen klik. Met een vertraging van 70 ms viel onze DOM-wijziging
         * daar bij elke snelle tik bovenop; bij een trage tik niet, en precies
         * dat maakte het zo grillig.
         *
         * Dus hangen we de balk meteen in, ruim vóór touchend. Zichtbaar wordt
         * hij er niet eerder van: de animatie begint op scaleX(0) en heeft een
         * eigen aanloop. De kaart blijft tijdens de aanraking onaangeroerd en
         * krijgt zijn 'bezig'-staat pas bij de klik.
         */
        showFeedback(/** @type {Element} */ (anchor), true);
        return;
      }

      armTimer = setTimeout(() => showFeedback(/** @type {Element} */ (anchor), false), ARM_DELAY_MS);
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'pointermove',
    (event) => {
      if (!pendingAnchor || confirmed) return;
      if (Math.abs(event.clientX - startX) > MOVE_CANCEL_PX || Math.abs(event.clientY - startY) > MOVE_CANCEL_PX) {
        clearFeedback();
      }
    },
    { capture: true, passive: true }
  );

  document.addEventListener('pointercancel', (event) => {
    if (!pendingAnchor || confirmed) return;

    // iOS: scroll-snap in een carrousel kan pointercancel sturen zonder echte
    // swipe. Dan verdwijnt de balk én blijft de click uit. Alleen in een
    // slideshow/carrousel én bij minimale beweging navigeren we zelf — elders
    // was dit een echte pan en moet de feedback weg.
    const anchor = pendingAnchor;
    const touch = event.pointerType === 'touch' || event.pointerType === 'pen';
    const inCarousel =
      anchor instanceof Element &&
      !!anchor.closest('slideshow-component, .resource-list--carousel, .resource-list__carousel');
    const barelyMoved =
      Math.abs(event.clientX - startX) <= MOVE_CANCEL_PX &&
      Math.abs(event.clientY - startY) <= MOVE_CANCEL_PX;

    if (
      touch &&
      inCarousel &&
      barelyMoved &&
      anchor instanceof HTMLAnchorElement &&
      navigatesAway(anchor) &&
      anchor.href
    ) {
      const href = anchor.href;
      clearTimeout(armTimer);
      clearTimeout(graceTimer);
      clearTimeout(touchNavTimer);
      touchNavTimer = setTimeout(() => {
        if (confirmed) return;
        if (pendingAnchor !== anchor) return;
        confirmed = true;
        showFeedback(anchor);
        killTimer = setTimeout(clearFeedback, BAR_MAX_MS);
        window.location.assign(href);
      }, TOUCH_NAV_FALLBACK_MS);
      return;
    }

    clearFeedback();
  });

  // De browser neemt het over voor scrollen: dit was geen tik op een link.
  // Alleen document/viewport-scroll; carrousel-scroll bubbelt niet naar window.
  window.addEventListener(
    'scroll',
    () => {
      if (pendingAnchor && !confirmed) clearFeedback();
    },
    { passive: true }
  );

  document.addEventListener(
    'pointerup',
    (event) => {
      if (!pendingAnchor || confirmed) return;

      const anchor = pendingAnchor;
      const touch = event.pointerType === 'touch' || event.pointerType === 'pen';

      // Volgt er geen klik (long-press, vinger buiten de link losgelaten), dan
      // blijft de feedback anders hangen.
      graceTimer = setTimeout(clearFeedback, CLICK_GRACE_MS);

      // Carrousels op iOS droppen vaak de eerste click door DOM-mutaties nà
      // touchend. Als de click uitblijft, navigeren we programmatisch.
      if (!touch || !(anchor instanceof HTMLAnchorElement) || !navigatesAway(anchor)) return;

      const upTarget = event.target;
      const stillOnLink =
        upTarget instanceof Node &&
        (anchor === upTarget || anchor.contains(upTarget) ||
          (upTarget instanceof Element && upTarget.closest('a[href]') === anchor));
      if (!stillOnLink) return;

      const href = anchor.href;
      clearTimeout(touchNavTimer);
      touchNavTimer = setTimeout(() => {
        if (confirmed) return;
        if (pendingAnchor !== anchor) return;
        confirmed = true;
        clearTimeout(graceTimer);
        showFeedback(anchor);
        killTimer = setTimeout(clearFeedback, BAR_MAX_MS);
        window.location.assign(href);
      }, TOUCH_NAV_FALLBACK_MS);
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'click',
    (event) => {
      const target = /** @type {Element | null} */ (event.target);
      const anchor = target && typeof target.closest === 'function' ? target.closest('a[href]') : null;

      const eligible =
        !event.defaultPrevented &&
        event.button === 0 &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.shiftKey &&
        !event.altKey &&
        navigatesAway(anchor) &&
        !controlIntercepts(target, anchor);

      if (!eligible) {
        clearFeedback();
        return;
      }

      // Toetsenbordnavigatie (Enter op een gefocuste link) kent geen
      // pointerdown; dan begint de feedback hier alsnog.
      confirmed = true;
      clearTimeout(graceTimer);
      clearTimeout(touchNavTimer);
      showFeedback(/** @type {Element} */ (anchor));
      killTimer = setTimeout(clearFeedback, BAR_MAX_MS);
    },
    { capture: true }
  );

  /*
   * Vangnet, in de bubble-fase en dus ná de handlers van de pagina. De
   * capture-listener hierboven moet beslissen op een moment dat nog niemand
   * `preventDefault` heeft kunnen aanroepen; alles wat de klik later afvangt —
   * de drag-lock die `slideshow.js` op document zet, of een app-block — zag hij
   * dus als een navigatie. Blijkt het event hier alsnog afgevangen, dan komt er
   * geen nieuwe pagina en moet de feedback weg in plaats van 15 seconden te
   * blijven hangen.
   */
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented) clearFeedback();
  });

  // GET-formulieren (zoekbalk, filters zonder JS) hebben dezelfde dode tijd.
  document.addEventListener(
    'submit',
    (event) => {
      if (event.defaultPrevented) return;
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.hasAttribute('data-no-nav-feedback')) return;
      if ((form.method || 'get').toLowerCase() !== 'get') return;

      confirmed = true;
      showFeedback(form);
      killTimer = setTimeout(clearFeedback, BAR_MAX_MS);
    },
    { capture: true }
  );

  // Navigatie afgebroken, of teruggekeerd via bfcache: staat opruimen zodat de
  // balk niet in de bfcache-snapshot blijft hangen.
  window.addEventListener('pagehide', clearFeedback);
  window.addEventListener('pageshow', clearFeedback);

  // iOS Safari past `:active` pas toe op de activation chain zodra er ergens
  // een touch-listener hangt. Zonder deze no-op blijft de pressed-state uit
  // juist op het grootste segment (37% van het verkeer is Safari mobiel).
  document.addEventListener('touchstart', function () {}, { passive: true });
})();
